<script setup lang="ts">
import { onMounted, onUnmounted, ref } from 'vue';

const props = withDefaults(
  defineProps<{
    title: string;
    message: string;
    getText?: (key: string) => string;
    confirmLabel?: string;
    cancelLabel?: string;
  }>(),
  {},
);

const emit = defineEmits<{
  confirm: [];
  close: [];
}>();

const dialogRef = ref<HTMLElement | null>(null);

const displayConfirmLabel = () => {
  if (props.confirmLabel) return props.confirmLabel;
  if (props.getText) return props.getText('confirmDialogConfirm');
  return 'Confirm';
};

const displayCancelLabel = () => {
  if (props.cancelLabel) return props.cancelLabel;
  if (props.getText) return props.getText('confirmDialogCancel');
  return 'Cancel';
};

function onConfirm(): void {
  emit('confirm');
}

function onClose(): void {
  emit('close');
}

// --- Keyboard ---
function onKeydown(e: KeyboardEvent): void {
  if (e.key === 'Escape') {
    e.preventDefault();
    onClose();
  }
}

onMounted(() => {
  document.addEventListener('keydown', onKeydown);
});

onUnmounted(() => {
  document.removeEventListener('keydown', onKeydown);
});
</script>

<template>
  <Teleport to="body">
    <div
      class="fixed inset-0 z-50 flex items-center justify-center bg-grey-900/40 px-lg"
    >
      <div
        ref="dialogRef"
        class="w-full max-w-sm bg-bg-card rounded-extra-large border border-border-light p-lg"
        data-testid="confirm-dialog"
        role="dialog"
        aria-modal="true"
        tabindex="-1"
      >
        <h2 class="text-title-medium font-semibold text-text-primary mb-sm">
          {{ title }}
        </h2>
        <p class="text-body-medium text-text-primary mb-lg">
          {{ message }}
        </p>
        <div class="flex justify-between items-center">
          <button
            type="button"
            class="px-md py-sm rounded-pill bg-bg-card border border-border-light text-text-secondary text-label-medium font-medium hover:bg-grey-200 active:bg-grey-300 focus-visible:ring-2 focus-visible:ring-brand-300 focus-visible:ring-offset-1 focus-visible:ring-offset-bg-page transition-colors duration-fast"
            @click="onClose"
          >
            {{ displayCancelLabel() }}
          </button>
          <button
            type="button"
            data-testid="confirm-yes-btn"
            class="px-md py-sm rounded-pill bg-brand text-grey-100 text-label-medium font-medium hover:opacity-90 focus-visible:ring-2 focus-visible:ring-brand-300 focus-visible:ring-offset-1 focus-visible:ring-offset-bg-page transition-opacity duration-fast"
            @click="onConfirm"
          >
            {{ displayConfirmLabel() }}
          </button>
        </div>
      </div>
    </div>
  </Teleport>
</template>
